import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { User } from "@/hooks/use-users";
import { useSetUserJobRole } from "@/hooks/use-users";
import { JOB_ROLES } from "@/lib/roles";

interface SetJobRoleDialogProps {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SetJobRoleDialog({ user, open, onOpenChange }: SetJobRoleDialogProps) {
  const [selectedRole, setSelectedRole] = useState<string>(user.jobRole ?? "");
  const setJobRole = useSetUserJobRole();

  function handleSave() {
    setJobRole.mutate(
      { userId: user.id, jobRole: selectedRole || null },
      { onSuccess: () => onOpenChange(false) },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Job Role</DialogTitle>
          <DialogDescription>Update the job role for {user.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="job-role-select">Job Role</Label>
          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger id="job-role-select" className="w-full">
              <SelectValue placeholder="No role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">No role</SelectItem>
              {JOB_ROLES.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  {role.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={setJobRole.isPending}>
            {setJobRole.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
